import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import axios from '../api/axios';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    username: '',
    password: '',
    role: 'user',
    assignedAdmins: []
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [assigningAdmin, setAssigningAdmin] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [quickAmounts, setQuickAmounts] = useState([]);
  const [savingAmounts, setSavingAmounts] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', phone: '', username: '', password: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [viewMode, setViewMode] = useState('assigned'); // 'assigned' | 'all'
  const [deletingUser, setDeletingUser] = useState(null); // user to confirm delete
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Decode JWT to get current admin's ID
  const getCurrentAdminId = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.id || payload._id || payload.sub || null;
    } catch { return null; }
  };
  const currentAdminId = getCurrentAdminId();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, adminsRes] = await Promise.all([
        axios.get('/admin/users'),
        axios.get('/admin/admins')
      ]);
      setUsers(usersRes.data);
      setAdmins(adminsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAdminToggle = (adminId) => {
    setFormData(prev => ({
      ...prev,
      assignedAdmins: prev.assignedAdmins.includes(adminId)
        ? prev.assignedAdmins.filter(id => id !== adminId)
        : [...prev.assignedAdmins, adminId]
    }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      await axios.post('/admin/users', formData);
      setFormData({
        name: '', phone: '', username: '', password: '',
        role: 'user', assignedAdmins: []
      });
      setShowForm(false);
      await fetchData();
      setActionLoading(false);
    } catch (error) {
      console.error('Error creating user:', error);
      setActionLoading(false);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      setActionLoading(true);
      await axios.patch(`/admin/users/${userId}`, { isActive: !currentStatus });
      await fetchData();
      setActionLoading(false);
    } catch (error) {
      console.error('Error updating user:', error);
      setActionLoading(false);
    }
  };

  const handleAssignAdmin = async (userId, adminId) => {
    try {
      await axios.patch(`/admin/users/${userId}/assign-admin`, { adminId });
      fetchData();
      setAssigningAdmin(null);
    } catch (err) {
      console.error('Failed to assign admin:', err);
    }
  };

  const fetchQuickAmounts = async (adminId) => {
    try {
      const res = await axios.get(`/admin/admins/${adminId}/quick-amounts`);
      setQuickAmounts(res.data.quickAmounts || []);
      setSelectedAdmin(adminId);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddAmount = () => {
    setQuickAmounts([...quickAmounts, { label: '', amount: '', imageUrl: '' }]);
  };

  const handleAmountChange = (index, field, value) => {
    const updated = [...quickAmounts];
    updated[index][field] = value;
    if (field === 'amount' && !updated[index].label) {
      updated[index].label = value;
    }
    setQuickAmounts(updated);
  };

  const handleRemoveAmount = (index) => {
    setQuickAmounts(quickAmounts.filter((_, i) => i !== index));
  };

  const handleAmountImageUpload = async (index, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      handleAmountChange(index, 'imageUrl', res.data.url);
    } catch (err) {
      console.error('Upload failed:', err);
    }
  };

  const handleSaveAmounts = async () => {
    setSavingAmounts(true);
    try {
      await axios.patch(`/admin/admins/${selectedAdmin}/quick-amounts`, { quickAmounts });
      alert('Quick amounts saved!');
      setSelectedAdmin(null);
      setQuickAmounts([]);
    } catch (err) {
      console.error(err);
      alert('Failed to save');
    }
    setSavingAmounts(false);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setEditForm({ name: user.name || '', phone: user.phone || '', username: user.username || '', password: '' });
    setEditError('');
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError('');
    try {
      const payload = { name: editForm.name, phone: editForm.phone, username: editForm.username };
      if (editForm.password) payload.password = editForm.password;
      await axios.patch(`/admin/users/${editingUser._id}`, payload);
      setEditingUser(null);
      await fetchData();
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`/admin/users/${deletingUser._id}`);
      setDeletingUser(null);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  const filteredUsers = viewMode === 'assigned'
    ? users.filter(u => u.assignedAdmins && u.assignedAdmins.some(a => (a._id || a) === currentAdminId))
    : users;

  return (
    <>
      <Navbar />
      <div className="page-wrapper">
        <div className="header-row">
          <div>
            <h1 className="page-title">Users & Admins</h1>
            <p className="page-subtitle">Manage user accounts, roles, assignments, and quick amounts.</p>
          </div>
          <button onClick={() => setShowForm(!showForm)} className="btn btn-primary">
            {showForm ? '✕ Close Form' : '+ Create New User'}
          </button>
        </div>

        {showForm && (
          <div className="glass-panel animate-in" style={{ padding: 32, marginBottom: 40 }}>
            <h2 style={{ margin: '0 0 24px 0', color: '#fff', fontSize: 20 }}>Create New User</h2>
            <form onSubmit={handleCreateUser}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20, marginBottom: 24 }}>
                <div>
                  <label className="input-label">Full Name</label>
                  <input type="text" name="name" className="input-field" value={formData.name} onChange={handleFormChange} required placeholder="John Doe" />
                </div>
                <div>
                  <label className="input-label">Phone Number</label>
                  <input type="text" name="phone" className="input-field" value={formData.phone} onChange={handleFormChange} required placeholder="+1234567890" />
                </div>
                <div>
                  <label className="input-label">Username</label>
                  <input type="text" name="username" className="input-field" value={formData.username} onChange={handleFormChange} required placeholder="johndoe" />
                </div>
                <div>
                  <label className="input-label">Password</label>
                  <input type="password" name="password" className="input-field" value={formData.password} onChange={handleFormChange} required placeholder="••••••••" />
                </div>
                <div>
                  <label className="input-label">Role</label>
                  <select name="role" className="input-field" value={formData.role} onChange={handleFormChange}>
                    <option value="user">Standard User</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>
              </div>

              {formData.role === 'user' && (
                <div style={{ marginBottom: 28, padding: 20, background: 'rgba(255,255,255,0.02)', borderRadius: 12, border: '1px solid var(--border)' }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 16 }}>ASSIGN ADMINS</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                    {admins.map(admin => (
                      <label key={admin._id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 12px', background: formData.assignedAdmins.includes(admin._id) ? 'rgba(59, 130, 246, 0.15)' : 'rgba(0,0,0,0.2)', borderRadius: 8, border: formData.assignedAdmins.includes(admin._id) ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid transparent', transition: 'all 0.2s' }}>
                        <input type="checkbox" checked={formData.assignedAdmins.includes(admin._id)} onChange={() => handleAdminToggle(admin._id)} style={{ accentColor: '#3b82f6', width: 16, height: 16 }} />
                        <span style={{ fontSize: 13, color: formData.assignedAdmins.includes(admin._id) ? '#fff' : 'var(--text-muted)' }}>{admin.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowForm(false)} className="btn btn-outline" style={{ minWidth: 120 }}>Cancel</button>
                <button type="submit" disabled={actionLoading} className="btn btn-success" style={{ minWidth: 160 }}>{actionLoading ? 'Creating...' : 'Create Account'}</button>
              </div>
            </form>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginBottom: 24, padding: 6, background: 'rgba(255,255,255,0.05)', borderRadius: 12, width: 'fit-content' }}>
          <button className={`btn-tab ${viewMode === 'assigned' ? 'active' : ''}`} onClick={() => setViewMode('assigned')}>
            <span style={{ marginRight: 6 }}>👥</span> My Users
          </button>
          <button className={`btn-tab ${viewMode === 'all' ? 'active' : ''}`} onClick={() => setViewMode('all')}>
            <span style={{ marginRight: 6 }}>🌍</span> All Users
          </button>
        </div>

        <div className="table-container mb-4">
          {loading ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>Loading users...</div>
          ) : (
            <table className="modern-table">
              <thead>
                <tr>
                  <th>User Info</th>
                  <th>Username / Phone</th>
                  <th>Role</th>
                  <th>Assigned Admin</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No users found in this view.</td></tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user._id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 600 }}>
                            {user.name?.[0]?.toUpperCase()}
                          </div>
                          <span style={{ fontWeight: 600, color: '#fff', fontSize: 15 }}>{user.name}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span style={{ color: '#fff', fontSize: 14 }}>@{user.username}</span>
                          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{user.phone}</span>
                        </div>
                      </td>
                      <td>
                        <span className={user.role === 'admin' ? 'badge badge-admin' : 'badge badge-success'} style={{ background: user.role === 'admin' ? 'rgba(59,130,246,0.1)' : 'rgba(22,163,74,0.1)' }}>
                          {user.role}
                        </span>
                      </td>
                      <td>
                        {assigningAdmin === user._id ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <select
                              className="input-field"
                              style={{ padding: '6px 30px 6px 10px', fontSize: 13 }}
                              value={user.assignedAdmins?.length > 0 ? user.assignedAdmins[0]._id : ''}
                              onChange={(e) => handleAssignAdmin(user._id, e.target.value || null)}
                            >
                              <option value="">None</option>
                              {admins.map(admin => <option key={admin._id} value={admin._id}>{admin.name}</option>)}
                            </select>
                            <button onClick={() => setAssigningAdmin(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>✕</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ color: user.assignedAdmins?.length > 0 ? '#fff' : 'var(--text-muted)', fontSize: 13 }}>
                              {user.assignedAdmins?.length > 0 ? user.assignedAdmins[0].name : 'Unassigned'}
                            </span>
                            <button onClick={() => setAssigningAdmin(user._id)} style={{ background: 'rgba(255,255,255,0.05)', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 11, color: '#fff', cursor: 'pointer' }}>Change</button>
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${user.isActive ? 'badge-active' : 'badge-inactive'}`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            className={`btn ${user.isActive ? 'btn-danger' : 'btn-success'}`}
                            onClick={() => handleToggleUserStatus(user._id, user.isActive)}
                            disabled={actionLoading}
                            style={{ padding: '6px 12px', fontSize: 12 }}
                          >
                            {user.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="btn btn-outline"
                            onClick={() => openEditModal(user)}
                            style={{ padding: '6px 12px', fontSize: 12 }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => setDeletingUser(user)}
                            style={{ padding: '6px 12px', fontSize: 12, background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)' }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Admins Table Component */}
        {!loading && (
          <div style={{ marginTop: 40 }}>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: '#fff', marginBottom: 20 }}>System Administrators</h2>
            <div className="table-container">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Admin Name</th>
                    <th>Phone</th>
                    <th>Assigned Users</th>
                    <th>Quick Amounts</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.length === 0 ? (
                    <tr><td colSpan="5" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No admins found.</td></tr>
                  ) : (
                    admins.map((admin) => (
                      <tr key={admin._id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'linear-gradient(135deg, #f59e0b, #d97706)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 600 }}>
                              {admin.name?.[0]?.toUpperCase()}
                            </div>
                            <span style={{ fontWeight: 600, color: '#fff', fontSize: 14 }}>{admin.name}</span>
                          </div>
                        </td>
                        <td style={{ color: 'var(--text-muted)' }}>{admin.phone}</td>
                        <td>
                          <span style={{ background: 'rgba(59,130,246,0.1)', color: '#60a5fa', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                            {admin.assignedUsers?.length || 0} users
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-primary" onClick={() => fetchQuickAmounts(admin._id)} style={{ padding: '6px 14px', fontSize: 12 }}>
                            Configure
                          </button>
                        </td>
                        <td>
                          <span className={`badge ${admin.isActive ? 'badge-active' : 'badge-inactive'}`}>
                            {admin.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modals */}
        {editingUser && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h2 style={{ margin: 0, color: '#fff', fontSize: 20 }}>Edit User Profile</h2>
                <button onClick={() => setEditingUser(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
              </div>

              <form onSubmit={handleEditUser}>
                {[
                  { label: 'Full Name', key: 'name', type: 'text', placeholder: 'John Doe' },
                  { label: 'Phone Number', key: 'phone', type: 'text', placeholder: '+1234567890' },
                  { label: 'Username', key: 'username', type: 'text', placeholder: 'johndoe' },
                  { label: 'New Password', key: 'password', type: 'password', placeholder: 'Leave blank to keep current' },
                ].map(({ label, key, type, placeholder }) => (
                  <div key={key} style={{ marginBottom: 20 }}>
                    <label className="input-label">{label}</label>
                    <input type={type} className="input-field" value={editForm[key]} onChange={e => setEditForm(prev => ({ ...prev, [key]: e.target.value }))} placeholder={placeholder} />
                  </div>
                ))}

                {editError && <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#f87171', padding: '12px 16px', borderRadius: 8, marginBottom: 24, fontSize: 13 }}>{editError}</div>}

                <div style={{ display: 'flex', gap: 12, marginTop: 32 }}>
                  <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setEditingUser(null)}>Cancel</button>
                  <button type="submit" disabled={editLoading} className="btn btn-primary" style={{ flex: 2 }}>{editLoading ? 'Saving...' : 'Save Changes'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deletingUser && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: 440 }}>
              <div style={{ textAlign: 'center', padding: '8px 0 24px' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
                <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 8 }}>Delete User?</h2>
                <p style={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6 }}>
                  Are you sure you want to permanently delete <strong style={{ color: '#fff' }}>{deletingUser.name}</strong>?
                  <br />This will delete all their messages and conversations. <strong style={{ color: '#f87171' }}>This cannot be undone.</strong>
                </p>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  className="btn btn-outline"
                  style={{ flex: 1 }}
                  onClick={() => setDeletingUser(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  style={{ flex: 1, background: 'rgba(239,68,68,0.2)', color: '#f87171', border: '1px solid rgba(239,68,68,0.4)' }}
                  onClick={handleDeleteUser}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? 'Deleting...' : '🗑️ Permanently Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedAdmin && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: 640 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                  <h2 style={{ margin: 0, color: '#fff', fontSize: 20 }}>Quick Amounts</h2>
                  <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 13 }}>Configuring for {admins.find(a => a._id === selectedAdmin)?.name}</p>
                </div>
                <button onClick={() => { setSelectedAdmin(null); setQuickAmounts([]); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
              </div>

              <div style={{ marginBottom: 24, maxHeight: '50vh', overflowY: 'auto', paddingRight: 8 }}>
                {quickAmounts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, border: '1px dashed var(--border)', borderRadius: 12, color: 'var(--text-muted)' }}>
                    No quick amounts configured yet.
                  </div>
                ) : (
                  quickAmounts.map((amount, index) => (
                    <div key={index} style={{ display: 'flex', gap: 16, marginBottom: 16, padding: 20, background: 'rgba(255,255,255,0.03)', borderRadius: 12, border: '1px solid var(--border)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 120px' }}>
                        <label className="input-label">Label</label>
                        <input type="text" className="input-field" value={amount.label} onChange={(e) => handleAmountChange(index, 'label', e.target.value)} placeholder="e.g. 1000" />
                      </div>
                      <div style={{ flex: '1 1 120px' }}>
                        <label className="input-label">Amount</label>
                        <input type="number" className="input-field" value={amount.amount} onChange={(e) => handleAmountChange(index, 'amount', e.target.value)} placeholder="0" />
                      </div>
                      <div style={{ flex: '2 1 160px' }}>
                        <label className="input-label">Image URL / Upload</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input type="text" className="input-field" value={amount.imageUrl} onChange={(e) => handleAmountChange(index, 'imageUrl', e.target.value)} placeholder="https://..." style={{ flex: 1 }} />
                          <label className="btn btn-outline" style={{ cursor: 'pointer', padding: '0 12px' }}>
                            <span style={{ fontSize: 16 }}>📎</span>
                            <input type="file" accept="image/*" onChange={(e) => { if (e.target.files?.[0]) handleAmountImageUpload(index, e.target.files[0]); }} style={{ display: 'none' }} />
                          </label>
                        </div>
                      </div>
                      <button className="btn btn-danger" onClick={() => handleRemoveAmount(index)} style={{ padding: '12px', alignSelf: 'flex-end', height: 44 }}>✕</button>
                    </div>
                  ))
                )}
              </div>

              <div style={{ display: 'flex', gap: 12, borderTop: '1px solid var(--border)', paddingTop: 24 }}>
                <button className="btn btn-outline" onClick={handleAddAmount} style={{ flex: 1 }}>+ Add Amount</button>
                <button className="btn btn-success" onClick={handleSaveAmounts} disabled={savingAmounts} style={{ flex: 2 }}>{savingAmounts ? 'Saving...' : 'Save Configuration'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Users;
